import * as blazeface from '@tensorflow-models/blazeface';

/**
 * High‑res face extraction using an already loaded BlazeFace model.
 * Adds ~30% padding around the detected face and encodes as high‑quality JPEG.
 *
 * NOTE: Call this with a base64 data URL and a BlazeFace model instance.
 */
export const extractFaceHighRes = async (
  base64Str: string,
  model: blazeface.BlazeFaceModel
): Promise<string> => {
  if (!base64Str || !model) {
    return base64Str;
  }

  try {
    const img = new Image();
    img.src = base64Str;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = (err) => reject(err);
    });

    const predictions = await model.estimateFaces(img, false);

    if (!predictions || predictions.length === 0) {
      console.warn('BlazeFace: no face detected, returning original image.');
      return base64Str;
    }

    const prediction = predictions[0];
    const start = prediction.topLeft as [number, number];
    const end = prediction.bottomRight as [number, number];

    let x = start[0];
    let y = start[1];
    let width = end[0] - start[0];
    let height = end[1] - start[1];

    // ADD PADDING with more emphasis on forehead:
    // - Horizontal: ~20% on each side
    // - Vertical: ~35% above, ~15% below
    const padLeftRight = 0.2;
    const padTop = 0.35;
    const padBottom = 0.15;

    let cropX = x - width * padLeftRight;
    let cropY = y - height * padTop;
    let cropWidth = width * (1 + padLeftRight * 2);
    let cropHeight = height * (1 + padTop + padBottom);

    // Clamp crop to image bounds
    if (cropX < 0) {
      cropWidth += cropX;
      cropX = 0;
    }
    if (cropY < 0) {
      cropHeight += cropY;
      cropY = 0;
    }
    const maxWidth = img.width;
    const maxHeight = img.height;
    if (cropX + cropWidth > maxWidth) {
      cropWidth = maxWidth - cropX;
    }
    if (cropY + cropHeight > maxHeight) {
      cropHeight = maxHeight - cropY;
    }

    const canvas = document.createElement('canvas');
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.warn('BlazeFace: no 2D context, returning original image.');
      return base64Str;
    }

    // Use image smoothing for better visual quality
    ctx.imageSmoothingEnabled = true;
    // @ts-ignore – not all TS dom libs include this property
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    const faceOnlyDataUrl = canvas.toDataURL('image/jpeg', 0.95);
    console.log('BlazeFace high-res face dataUrl length:', faceOnlyDataUrl.length);
    console.log('BlazeFace high-res face dataUrl preview:', faceOnlyDataUrl.substring(0, 120));

    return faceOnlyDataUrl;
  } catch (err) {
    console.warn('BlazeFace high-res face extraction failed, returning original image.', err);
    return base64Str;
  }
};

